import { Component, effect, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute, Params } from '@angular/router'
import { SchemaUtil, historyBack, unknownMimeType } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { ProductMovePack } from 'depoto-core/src/entities/ProductMovePack'
import { File, ProductMove, Tag } from 'depoto-core/src/entities'
import { ModalSerialNumbersComponent } from '../../modal-serial-numbers/modal-serial-numbers.component'
import { TranslateService } from '@ngx-translate/core'
import { PrintCodeComponent } from '../../components/print-code/print-code.component'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { getServerUrl } from '../../services/ApiUtils'

const productMovePackSchema = {
  id: null,
  externalId: null,
  externalReference: null,
  type: {
    id: null,
    name: null,
  },
  number: null,
  url: null,
  pdfWOPricesUrl: null,
  moves: {
    id: null,
    amount: null,
    note: null,
    product: {
      id: null,
      name: null,
      fullName: null,
      code: null,
      ean: null,
      requiredSerialNumbers: null,
    },
    serialNumbers: null,
    orderItem: {
      id: null,
      product: {
        id: null,
        fullName: null,
        code: null,
        ean: null,
        mainImage: {
          url: null,
          thumbnails: {
            format: null,
            url: null,
          },
        },
        quantityUnavailable: null,
      },
      priceWithoutVat: null,
    },
    productDepotFrom: {
      id: null,
      batch: null,
      expirationDate: null,
      position: null,
      supplier: {
        id: null,
        name: null,
      },
      purchasePrice: null,
      product: {
        id: null,
        fullName: null,
        code: null,
        ean: null,
        mainImage: {
          url: null,
          thumbnails: {
            format: null,
            url: null,
          },
        },
        vat: {
          percent: null,
        },
      },
    },
    productDepotTo: {
      id: null,
      batch: null,
      expirationDate: null,
      position: null,
      supplier: {
        id: null,
        name: null,
      },
      purchasePrice: null,
      product: {
        id: null,
        fullName: null,
        code: null,
        ean: null,
        mainImage: {
          url: null,
          thumbnails: {
            format: null,
            url: null,
          },
        },
      },
    },
    deletable: null,
  },
  deletable: null,
  dateCreated: null,
  order: {
    id: null,
    reservationNumber: null,
    currency: {
      id: null,
      name: null,
    },
    checkout: {
      id: null,
      name: null,
    },
    vs: null,
    status: {
      id: null,
    },
    note: null,
    privateNote: null,
  },
  purchaseOrder: {
    id: null,
    number: null,
  },
  createdBy: {
    id: null,
    firstName: null,
    lastName: null,
    email: null,
  },
  depotFrom: {
    id: null,
    name: null,
  },
  depotTo: {
    id: null,
    name: null,
  },
  tags: {
    id: null,
    name: null,
    type: null, //todo ?
    color: null,
  },
  note: null,
  files: {
    id: null,
    mimeType: null,
    originalFilename: null,
    url: null,
  },
}

@Component({
  selector: 'product-move-pack-page',
  templateUrl: 'product-move-pack.component.html',
  styleUrls: ['product-move-pack.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductMovePackComponent {
  tagService = inject(TagService)
  @ViewChild(ModalSerialNumbersComponent) modalSerialNumbers: ModalSerialNumbersComponent
  @ViewChild('printCodeComponent') printCodeComponent: PrintCodeComponent
  private queryClient = injectQueryClient()

  productMovePack: ProductMovePack
  currencySymbol: string
  canCreateOrEdit = false
  loading = true
  quantityItemsSum: number[] | number = 0
  userCanDeleteProductMoves = false
  isPrintingEANs = false
  isAnyOfMovesDeletable = false
  isRequiredSerialNumbers = false
  hasOrderItems = false
  lastSavedNote = ''
  isDownloadingOrionEdi = false

  readonly queryTagsOptions = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'product_move_pack'),
  }))

  readonly mutationUpdateNote = injectMutation(() => ({
    mutationFn: async (note: string) => {
      if (!this.productMovePack?.id) {
        throw new Error('Product move pack is not loaded')
      }
      const result = await this.core.services.productMovePack.updateNote(
        {
          id: this.productMovePack.id,
          note,
        },
        {
          id: null,
          note: null,
          url: null,
          pdfWOPricesUrl: null,
        }
      )
      if (!result) {
        throw new Error('Product move pack note was not updated')
      }
      return { result, note }
    },
    onSuccess: ({ result, note }) => {
      this.productMovePack.note = this.normalizeNote(result?.note ?? note)
      this.productMovePack.url = result?.url ?? this.productMovePack.url
      this.productMovePack.pdfWOPricesUrl = result?.pdfWOPricesUrl ?? this.productMovePack.pdfWOPricesUrl
      this.lastSavedNote = this.productMovePack.note
      this.notificationService.success(this.translateService.instant('alert.product.move.pack.note.updated'))
      this.queryClient.invalidateQueries({ queryKey: ['productMovePacks'] })
    },
  }))

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    this.route.params.forEach((params: Params) => {
      const id = +params['id']
      this.refreshData(id)
    })
    effect(() => {
      this.queryTagsOptions.data()
    })
  }

  unknownType(s?: string) {
    return unknownMimeType(s)
  }
  async refreshData(id: number) {
    this.loading = true
    try {
      const result = await this.core.services.productMovePack.getById(id, productMovePackSchema)
      this.userCanDeleteProductMoves =
        this.core.services.user.hasRole('ROLE_PRODUCT_MOVE_PACK_DELETE') ||
        this.core.services.user.hasRole('ROLE_ADMIN') ||
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
      if (result) {
        this.productMovePack = new ProductMovePack(result)
        // Update title with productMovePack name todo ?
        if (this.productMovePack) {
          this.titleService.updateTitleWithObject(
            this.productMovePack.id,
            String(this.productMovePack.number),
            'title.product-move-pack-id'
          )
        }
        this.productMovePack.note = this.normalizeNote(this.productMovePack.note)
        this.lastSavedNote = this.productMovePack.note
        this.productMovePack.moves.map(m => ({ ...m, isEANPrintable: true }))
        this.currencySymbol = this.translateService.instant(
          this.productMovePack.order?.currency?.id
            ? this.translateService.instant(this.productMovePack.order.currency.name)
            : this.core.services.user?.user?.company?.currency?.name
        )
        const movesAmounts = this.productMovePack.moves?.map(i => i.amount)
        this.quantityItemsSum = movesAmounts?.length ? movesAmounts.reduce((a, b) => Number(a) + Number(b)) : 0
        this.printCodeComponent?.hydrate()
        // set props for showing th, td
        this.isAnyOfMovesDeletable = this.productMovePack.moves.some(move => move.deletable)
        this.isRequiredSerialNumbers = this.productMovePack.moves.some(move => move.product?.requiredSerialNumbers)
        this.hasOrderItems = this.productMovePack.moves.some(move => move.orderItem)
      }
    } catch (e) {
      console.warn(e)
    } finally {
      this.loading = false
    }
  }

  isNoteChanged(): boolean {
    return this.normalizeNote(this.productMovePack?.note) !== this.lastSavedNote
  }

  async updateNote() {
    if (!this.productMovePack || !this.isNoteChanged() || this.mutationUpdateNote.isPending()) {
      return
    }
    this.mutationUpdateNote.mutate(this.normalizeNote(this.productMovePack.note))
  }

  async updateProductMovePack() {
    this.loading = true
    try {
      this.productMovePack = await this.core.services.productMovePack.update(
        this.productMovePack,
        SchemaUtil.productMovePack
      )
    } catch (e) {
      console.warn(e)
    } finally {
      this.loading = false
      this.modalSerialNumbers.hideChildModal()
    }
  }

  async updateTags() {
    this.loading = true
    try {
      const productMovePackTags = { ...this.productMovePack, tags: this.productMovePack.tags }
      await this.core.services.productMovePack
        .updateTags(productMovePackTags, {
          id: null,
          tags: {
            id: null,
            name: null,
            color: null,
          },
        })
        .then(result => {
          this.productMovePack.tags = result.tags
        })
    } catch (e) {
      console.warn(e)
    } finally {
      this.loading = false
    }
  }

  getAmountSum() {
    if (this.productMovePack.moves.every(move => move.orderItem?.id)) {
      return this.productMovePack.moves.reduce((total, move) => {
        return total + move.orderItem.priceWithoutVat * move.amount
      }, 0)
    } else if (this.productMovePack.type.id === 'out') {
      return this.productMovePack.moves.reduce((total, move) => {
        return total + move.productDepotFrom.purchasePrice * move.amount
      }, 0)
    } else if (this.productMovePack.type.id === 'in') {
      return this.productMovePack.moves.reduce((total, move) => {
        return total + move.productDepotTo.purchasePrice * move.amount
      }, 0)
    } else if (this.productMovePack.moves[0].productDepotFrom) {
      return this.productMovePack.moves.reduce((total, move) => {
        return total + move.productDepotFrom.purchasePrice * move.amount
      }, 0)
    } else if (this.productMovePack.moves[0].productDepotTo) {
      return this.productMovePack.moves.reduce((total, move) => {
        return total + move.productDepotTo.purchasePrice * move.amount
      }, 0)
    }
  }

  getQuantitySum() {
    return this.productMovePack.moves.reduce((currentTotal, move) => move.amount + currentTotal, 0)
  }

  public async deleteProductMovePack() {
    if (!confirm(this.translateService.instant('delete.confirmation'))) {
      return new Promise<void>(r => r())
    }
    const returnProductId = this.getReturnProductId()
    this.loading = true
    try {
      //TODO: why do we rely on name?
      const alert = this.productMovePack.type.name.includes('Výdejka')
        ? 'alert.product.move.pack.out.deleted'
        : this.productMovePack.type.name.includes('Příjemka')
          ? 'alert.product.move.pack.in.deleted'
          : 'alert.product.move.pack.transfer.deleted'
      const deletionErrors = await this.core.services.productMovePack.delete(this.productMovePack)
      if (deletionErrors !== null) return

      this.notificationService.success(this.translateService.instant(alert))
      await Promise.all([
        this.queryClient.invalidateQueries({ queryKey: ['productMovePacks'], refetchType: 'all' }),
        this.queryClient.invalidateQueries({ queryKey: ['productMoves'], refetchType: 'all' }),
      ])
      await this.router.navigate(returnProductId ? ['/product', returnProductId, 'moves'] : ['/product-move-packs'], {
        replaceUrl: true,
      })
    } catch (e) {
      console.warn(e)
    } finally {
      this.loading = false
    }
  }

  public async deleteProductMove(move: ProductMove) {
    if (!confirm(this.translateService.instant('productMovePack.delete.confirmation'))) {
      return new Promise<void>(r => r())
    }
    this.loading = true
    try {
      await this.core.services.productMove.delete(move).then(() => {
        this.notificationService.success(this.translateService.instant('alert.product.move.deleted'))
        this.refreshData(this.productMovePack.id)
      })
    } catch (e) {
      console.warn(e)
    }
    this.loading = false
  }

  goBack(): void {
    historyBack()
  }

  downloadPdf(type: 'issueSlip' | 'deliveryNote'): void {
    const urlType = type === 'issueSlip' ? 'url' : 'pdfWOPricesUrl'
    if (!this.productMovePack || !this.productMovePack[urlType]?.length) {
      return
    }
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.productMovePack[urlType])
  }

  downloadExcel(type: 'serialNumbers'): void {
    if (type === 'serialNumbers') {
      const url = `${getServerUrl(this.core.services.oauth.session)}export/pmp-serial-numbers/${this.productMovePack.id}`
      // Not using download.downloadDocumentAndShowInNewWindow: this export's response mime type doesn't match
      // any of its known excel cases, so it falls back to opening the blob in a tab as if it were a PDF - Chrome
      // force-downloads unrenderable blobs while Firefox renders the raw bytes inline as garbled text.
      this.core.services.http
        .downloadBlob(url)
        .then((blob: Blob) => {
          const dataUrl = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = dataUrl
          a.download = `pmp-serial-numbers-${this.productMovePack.id}.xlsx`
          document.body.appendChild(a)
          a.click()
          a.remove()
          setTimeout(() => window.URL.revokeObjectURL(dataUrl), 120 * 1000)
        })
        .catch(() => this.notificationService.error(this.translateService.instant('alert.usergroup.error')))
    }
  }

  canDownloadOrionEdi(): boolean {
    return Boolean(
      this.productMovePack?.type?.id === 'out' && this.productMovePack?.order?.id && this.productMovePack?.moves?.length
    )
  }

  async downloadOrionEdi(): Promise<void> {
    if (!this.canDownloadOrionEdi() || this.isDownloadingOrionEdi) {
      return
    }

    this.isDownloadingOrionEdi = true
    try {
      const url = `${getServerUrl(this.core.services.oauth.session)}export/pmp-orion-edi/${this.productMovePack.id}`
      const blob = await this.core.services.http.downloadBlob(url)
      const mimeType = blob.type.toLowerCase().split(';', 1)[0]
      if (mimeType !== 'application/xml' && mimeType !== 'text/xml') {
        throw new Error(`Unexpected Orion EDI response type: ${blob.type || 'unknown'}`)
      }

      const dataUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = dataUrl
      a.download = `orion-edi-${this.productMovePack.number}.xml`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => window.URL.revokeObjectURL(dataUrl), 120 * 1000)
    } catch {
      this.notificationService.error(this.translateService.instant('alert.orion-edi.download-error'))
    } finally {
      this.isDownloadingOrionEdi = false
    }
  }

  getThumbUrl(image: File): string {
    return image.thumbnails?.length > 0 ? image.thumbnails.filter(t => t.format === 'w135wp')[0].url : ''
  }

  private normalizeNote(note?: string): string {
    return note ?? ''
  }

  private getReturnProductId(): number | null {
    const productId = Number(this.route.snapshot.queryParamMap.get('returnProductId'))
    return Number.isInteger(productId) && productId > 0 ? productId : null
  }
}
